import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../../common/Mypage.scss';
import '../../../recruit/common/Recruit.scss';
import Loading from '../../../../components/Loading';
import MypageMenu from '../../common/MypageMenu';
import { MINISTER_RECRUIT_CONFIG, OVERSEAS_RECRUIT_CONFIG } from '../../../recruit/common/recruitConfigs';
import {
  churchApi,
  errorMessage,
  relativeDay,
  type ApplicationCounts,
  type ApplicationGroup,
  type ChurchListItem,
  type JobSummary,
} from '../../applications/applicationStatusApi';
import { GroupTabs, StatusBadge } from '../../applications/applicationUi';
import ChurchStatusActions from '../../applications/ChurchStatusActions';

// 교회 지원자 관리 보드 (application-status-design.md 6장)
// 칸반 대신 리스트 + 행별 원클릭 버튼. 버튼은 서버가 계산한 "가능한 다음 상태"만.

function deadlineText(job: JobSummary | null) {
  if (!job?.deadlineAt || job.daysToDeadline == null) return null;
  if (job.daysToDeadline < 0) return '마감됨';
  if (job.daysToDeadline === 0) return '오늘 마감';
  return `마감 ${job.daysToDeadline}일 전`;
}

export default function ApplicantManage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const recruitId = params.get('id') || '';
  const recruitType = params.get('tableType') === 'overseas' ? 'overseas' : 'minister';
  const recruitConfig = recruitType === 'overseas' ? OVERSEAS_RECRUIT_CONFIG : MINISTER_RECRUIT_CONFIG;

  const [group, setGroup] = useState<ApplicationGroup>('all');
  const [job, setJob] = useState<JobSummary | null>(null);
  const [items, setItems] = useState<ChurchListItem[]>([]);
  const [counts, setCounts] = useState<ApplicationCounts | null>(null);
  const [showBulkView, setShowBulkView] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!recruitId) return;
    try {
      const data = await churchApi.board(recruitType, recruitId, group);
      setJob(data.job);
      setItems(data.items);
      setCounts(data.counts);
      setShowBulkView(data.showBulkView);
      setError('');
    } catch (e) {
      setError(errorMessage(e, '지원자 목록을 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, [recruitId, recruitType, group]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const openResume = (applicationId: number) => {
    navigate(`/mypage/applicantresume?id=${applicationId}`);
    window.scrollTo(0, 0);
  };

  const bulkView = async () => {
    const ids = items.filter((i) => i.status === 'SUBMITTED').map((i) => i.id);
    if (!ids.length) return;
    try {
      await churchApi.bulkView(ids);
      load();
    } catch (e) {
      alert(errorMessage(e));
    }
  };

  const deadline = deadlineText(job);
  const unviewed = items.filter((i) => i.status === 'SUBMITTED').length;

  return (
    <div className="mypage">
      <div className="inner">
        <MypageMenu />
        <div className="subpage__main">
          <div className="mypage-panel mypage-section-card">
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
                <Loading />
              </div>
            ) : error ? (
              <div className="app-empty">
                <p>{error}</p>
                <button type="button" className="app-btn" onClick={() => navigate('/mypage/postingmanage')}>
                  목록
                </button>
              </div>
            ) : (
              <>
                <div className="app-board-head">
                  <div className="main_title_row">
                    <h3>
                      {job?.title || '공고'} — 지원자 {counts?.all ?? 0}명
                    </h3>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      {!job?.deleted && !job?.closed && (
                        <div className="list-btn" onClick={() => navigate(`/mypage/talent?id=${recruitId}&tableType=${recruitType}`)}>
                          <span>인재 검색</span>
                        </div>
                      )}
                      <div className="list-btn" onClick={() => navigate(`${recruitConfig.detailPath}/${recruitId}`)}>
                        <span>공고보기</span>
                      </div>
                      <div className="list-btn" onClick={() => navigate('/mypage/postingmanage')}>
                        <span>목록</span>
                      </div>
                    </div>
                  </div>
                  <p>
                    {[job?.church, job?.region].filter(Boolean).join(' · ')}
                    {deadline && (
                      <>
                        {' · '}
                        <span className="app-board-head__deadline">{deadline}</span>
                      </>
                    )}
                  </p>
                  {/* 지원자 0명인 공고에는 사례비·마감일 공개를 권하는 넛지 (10장 10번) */}
                  {(counts?.all ?? 0) === 0 && (job?.payMissing || job?.deadlineMissing) && (
                    <p className="app-board-head__warn">
                      {[job?.payMissing && '사례비', job?.deadlineMissing && '마감일'].filter(Boolean).join('·')}이(가) 비어 있습니다.
                      공개한 공고에 지원이 더 많이 들어옵니다.
                    </p>
                  )}
                </div>

                {(counts?.all ?? 0) > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <GroupTabs value={group} counts={counts} onChange={setGroup} />
                    {showBulkView && unviewed > 0 && (
                      <button type="button" className="app-btn app-btn--small" onClick={bulkView}>
                        미열람 {unviewed}명 일괄 열람 처리
                      </button>
                    )}
                  </div>
                )}

                {items.length === 0 ? (
                  <div className="app-empty">
                    <p>{(counts?.all ?? 0) === 0 ? '아직 지원한 사람이 없습니다.' : '이 그룹에 해당하는 지원자가 없습니다.'}</p>
                  </div>
                ) : (
                  <div className="app-list">
                    {items.map((item) => {
                      const a = item.applicant;
                      const profile = [a.gender, a.age ? `${a.age}세` : ''].filter(Boolean).join('·');
                      const info = [a.region, a.careerYears ? `경력 ${a.careerYears}년` : '', a.education].filter(Boolean).join(' · ');
                      const prefs = [a.prefs.roles, a.prefs.depts].filter(Boolean).join(' / ');
                      return (
                        <div key={item.id} className={`app-board-row${item.group === 'closed' ? ' is-closed' : ''}`}>
                          <div className="app-board-row__top">
                            <strong>{a.name}</strong>
                            {profile && <span className="app-board-row__sub">{profile}</span>}
                            <StatusBadge status={item.status} />
                            <span className="app-board-row__when">{relativeDay(item.statusChangedAt)}</span>
                          </div>
                          {info && <div className="app-board-row__info">{info}</div>}
                          {prefs && <div className="app-board-row__info">희망: {prefs}</div>}
                          {a.contact && (
                            <div className="app-board-row__info app-board-row__contact">
                              {[a.contact.phone, a.contact.email].filter(Boolean).join(' · ')}
                            </div>
                          )}
                          <div className="app-board-row__buttons">
                            <button type="button" className="app-btn app-btn--small app-btn--primary" onClick={() => openResume(item.id)}>
                              이력서
                            </button>
                            <ChurchStatusActions
                              small
                              applicationId={item.id}
                              version={item.version}
                              actions={item.actions}
                              applicantName={a.name}
                              onChanged={load}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <p style={{ marginTop: 12, color: 'var(--ink-4)', fontSize: 15 }}>
                  지원자의 실명과 연락처는 '연락'을 누른 이후에 공개됩니다. 이력서는 지원 시점 기준으로 고정되어 있습니다.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
